import React, { useEffect, useMemo } from 'react';
import { useGetInventorySummaryQuery, useAddRemoveIgnoredOptimizationsMutation } from '../../api/discovery/inventory';
import { useNotificationContext } from '../../providers/NotificationProvider';

/**
 * Encapsulates all data-fetching, loading-state, and derived-value logic for
 * the SummaryWrapper. Returns stable values that drive the UI.
 */
export const useInventorySummaryData = ({ setDashboardHasNoData = () => {}, canRenderImportButton = false } = {}) => {
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);

    const { notifySuccess, notifyError } = useNotificationContext();
    const [ignoreOptimization] = useAddRemoveIgnoredOptimizationsMutation();

    const {
        data: summaryData,
        isLoading,
        isFetching,
        isSuccess,
    } = useGetInventorySummaryQuery(orgId, {
        skip: !orgId,
        refetchOnMountOrArgChange: true,
    });

    // True only when API response contains at least one meaningful count field
    const isDataReady = useMemo(() => {
        if (!isSuccess || !summaryData) return false;
        const hasCountData =
            summaryData.count && typeof summaryData.count === 'object' && Object.keys(summaryData.count).length > 0;
        const hasReadyData =
            summaryData.ready && typeof summaryData.ready === 'object' && Object.keys(summaryData.ready).length > 0;
        return hasCountData || hasReadyData;
    }, [isSuccess, summaryData]);

    const inventoryTotals = useMemo(() => {
        if (!isDataReady) return { totalInventory: 0, totalReady: 0 };
        const totalInventory = summaryData?.count
            ? Object.values(summaryData.count).reduce((sum, val) => sum + (val?.total ?? val ?? 0), 0)
            : 0;
        const totalReady = summaryData?.ready ? Object.values(summaryData.ready).reduce((sum, val) => sum + val, 0) : 0;
        return { totalInventory, totalReady };
    }, [isDataReady, summaryData]);

    const hasNoData = useMemo(
        () => isDataReady && inventoryTotals.totalInventory === 0 && inventoryTotals.totalReady === 0,
        [isDataReady, inventoryTotals],
    );

    useEffect(() => {
        if (canRenderImportButton) {
            setDashboardHasNoData(hasNoData);
        }
    }, [hasNoData, setDashboardHasNoData, canRenderImportButton]);

    /**
     * Calls the ignore-optimizations mutation. Returns true on success, false on
     * failure (errors are surfaced via notifications, not thrown).
     */
    const handleIgnoreOptimizations = async (optimizationAction) => {
        try {
            await ignoreOptimization({ orgId, action: 'add', optimizations: optimizationAction }).unwrap();
            notifySuccess({ message: 'Optimizations ignored successfully.' });
            return true;
        } catch (error) {
            notifyError({ message: 'Failed to ignore optimizations.' });
            console.error('Error ignoring optimizations:', error);
            return false;
        }
    };

    return {
        summaryData,
        isLoading,
        isFetching,
        isDataReady,
        inventoryTotals,
        hasNoData,
        handleIgnoreOptimizations,
    };
};
